<template>
  <v-app>
    <Snackbar />
    <ResendInvitationsDialog />
    <RouterView></RouterView>
    <SessionInvalidDialog />
    <DeepReloadPrompt v-if="!isLocalEnv()" />
    <DevSettingsDrawer v-if="isLocalOrDevEnv()" />
  </v-app>
</template>

<script setup lang="ts">
import Snackbar from '@/components/snackbar/Snackbar.vue';
import ResendInvitationsDialog from '@/components/dialogs/ResendInvitationsDialog.vue';
import SessionInvalidDialog from '@/components/dialogs/SessionInvalidDialog.vue';
import DevSettingsDrawer from '@/components/drawers/DevSettingsDrawer.vue';
import { usePreferencesStore } from '@/stores/deepadmin/preferences.ts';
import { useLocalPreferencesStore } from '@/stores/localPreferences.ts';
import AuthService from '@/services/authService.ts';
import { PreferenceOptions } from '@/types/enums/PreferenceOptions.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { computed } from 'vue';
import { globalRouterReference } from '@/router/globalRouterReference';
import { useRouter } from 'vue-router';
import { useAppLocale } from '@/composables/useAppLocale';
import { DeepReloadPrompt } from '@deepcloud/deep-ui-lib';
import { isLocalEnv, isLocalOrDevEnv } from '@/utils/env.ts';
import { useApi } from '@/composables/useApi.ts';
import { useHeadSafe } from '@unhead/vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { deepAdminAPI } = useApi();
const preferencesStore = usePreferencesStore();
const localPreferencesStore = useLocalPreferencesStore();
globalRouterReference.router = useRouter();

const fallbackSeoDescription =
  'Sign your documents online from anywhere. DeepSign is flexible, secure and legally valid in EU and Switzerland.';

const description = computed(() => {
  const translated = t('seo.description');
  return translated && translated !== 'seo.description'
    ? translated
    : fallbackSeoDescription;
});

useAppLocale();

// Fetch preferences whenever the user is authenticated, even while on the
// public document access route (which also carries a document-scoped auth
// token): recently used email suggestions are tied to the logged-in user,
// not the document token, so they should still be available there.
if (AuthService.isAuthenticated) {
  preferencesStore.fetchAll().then((response) => {
    const preferences = response.data.preferences;
    // Move viewOption / expertSettings / lastSelectedOrg / documentsWizards onto
    // this device and drop them from the capped profile payload.
    void localPreferencesStore.migrateFromProfile(preferences);
    // check if a user has a default AuthorityService set
    if (!preferences.defaultAuthorityService && AuthService.user?.sub) {
      // otherwise check if user as a valid Mobile ID, if true MobileID becomes the default value
      const userId = AuthService.user.sub;
      deepAdminAPI.value.users
        .getById(userId, { params: { mobileId: true } })
        .then((response) => {
          if (response.data && response.data.is_mobile_id) {
            preferencesStore.setPreference(
              PreferenceOptions.DEFAULT_AUTHORITY_SERVICE,
              AuthorityService.MOBILE_ID
            );
          }
        });
    }
  });
}
useHeadSafe({
  meta: [
    {
      name: 'description',
      content: () => description.value
    },
    {
      property: 'og:description',
      content: () => description.value
    }
  ]
});
</script>
